<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';

import ReportHeader from './components/ReportHeader.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import Spinner from 'shared/components/Spinner.vue';
import LeadsReportsAPI from 'dashboard/api/leadsReports';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const reportData = ref(null);
const strategies = ref([]);
const selectedStrategy = ref(null);
const selectedFunnel = ref(null);
const groupBy = ref('day');

const currentDateRangeSelection = ref({
  id: 0,
  name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
});
const customDateRange = ref([new Date(), new Date()]);

const CUSTOM_DATE_RANGE_ID = 5;

const dateRange = computed(() => [
  { id: 0, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') },
  { id: 1, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS') },
  { id: 2, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS') },
  { id: 3, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS') },
  { id: 4, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_YEAR') },
  { id: 5, name: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') },
]);

const groupByOptions = computed(() => [
  { id: 'day', name: t('REPORT.GROUPING_OPTIONS.DAY') },
  { id: 'week', name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  { id: 'month', name: t('REPORT.GROUPING_OPTIONS.MONTH') },
]);

const isDateRangeSelected = computed(
  () => currentDateRangeSelection.value.id === CUSTOM_DATE_RANGE_ID
);

const funnels = computed(() => store.getters['funnels/getFunnels'] || []);

const fromDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(startOfDay(customDateRange.value[0]));
  }
  const dateRangeMap = { 0: 6, 1: 29, 2: 89, 3: 179, 4: 364 };
  const diff = dateRangeMap[currentDateRangeSelection.value.id];
  return getUnixTime(startOfDay(subDays(new Date(), diff)));
});

const toDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(endOfDay(customDateRange.value[1]));
  }
  return getUnixTime(endOfDay(new Date()));
});

const chartData = computed(() => {
  if (!reportData.value?.by_date) return [];

  const entries = Object.entries(reportData.value.by_date);
  return entries
    .map(([date, count]) => ({
      date: formatDate(date),
      rawDate: date,
      count,
    }))
    .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
});

const strategyBreakdown = computed(() => {
  if (!reportData.value?.by_strategy) return [];
  return reportData.value.by_strategy;
});

const summary = computed(() => reportData.value?.summary || {});

const maxCount = computed(() => {
  if (!chartData.value.length) return 0;
  return Math.max(...chartData.value.map(d => d.count));
});

function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

function getBarHeight(count) {
  if (!maxCount.value) return 0;
  return (count / maxCount.value) * 100;
}

async function fetchStrategies() {
  try {
    const response = await LeadsReportsAPI.getStrategies();
    strategies.value = [
      { id: null, name: t('LEADS_REPORTS.ALL_STRATEGIES') },
      ...response.data,
    ];
  } catch (error) {
    console.error('Error fetching strategies:', error);
  }
}

async function fetchFunnels() {
  await store.dispatch('funnels/fetchFunnels');
}

async function fetchReport() {
  isLoading.value = true;
  try {
    const response = await LeadsReportsAPI.getLeadsReport({
      from: fromDate.value,
      to: toDate.value,
      strategyId: selectedStrategy.value?.id,
      funnelId: selectedFunnel.value?.id,
      groupBy: groupBy.value,
    });
    reportData.value = response.data;
  } catch (error) {
    console.error('Error fetching leads report:', error);
  } finally {
    isLoading.value = false;
  }
}

function onDateRangeChange(selectedRange) {
  currentDateRangeSelection.value = selectedRange;
  fetchReport();
}

function onCustomDateChange(value) {
  customDateRange.value = value;
  fetchReport();
}

function onStrategyChange() {
  fetchReport();
}

function onFunnelChange() {
  fetchReport();
}

function onGroupByChange() {
  fetchReport();
}

onMounted(async () => {
  await Promise.all([fetchStrategies(), fetchFunnels()]);
  fetchReport();
});

watch([fromDate, toDate], () => {
  // Date change handled in specific methods
});
</script>

<template>
  <div>
    <ReportHeader :header-title="$t('LEADS_REPORTS.HEADER')" />

    <!-- Filters -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <!-- Strategy Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('LEADS_REPORTS.FILTER_STRATEGY') }}
        </p>
        <multiselect
          v-model="selectedStrategy"
          track-by="id"
          label="name"
          :placeholder="$t('LEADS_REPORTS.SELECT_STRATEGY')"
          :options="strategies"
          :searchable="true"
          :allow-empty="true"
          @update:model-value="onStrategyChange"
        />
      </div>

      <!-- Funnel Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('LEADS_REPORTS.FILTER_FUNNEL') }}
        </p>
        <multiselect
          v-model="selectedFunnel"
          track-by="id"
          label="title"
          :placeholder="$t('LEADS_REPORTS.SELECT_FUNNEL')"
          :options="funnels"
          :searchable="true"
          :allow-empty="true"
          @update:model-value="onFunnelChange"
        />
      </div>

      <!-- Date Range Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('REPORT.DURATION_FILTER_LABEL') }}
        </p>
        <multiselect
          v-model="currentDateRangeSelection"
          track-by="id"
          label="name"
          :options="dateRange"
          :searchable="false"
          :allow-empty="false"
          @select="onDateRangeChange"
        />
      </div>

      <!-- Group By Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL') }}
        </p>
        <multiselect
          v-model="groupBy"
          track-by="id"
          label="name"
          :options="groupByOptions"
          :searchable="false"
          :allow-empty="false"
          @update:model-value="onGroupByChange"
        >
          <template #singleLabel="{ option }">
            {{ option.name || $t('REPORT.GROUPING_OPTIONS.DAY') }}
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Custom Date Range -->
    <div v-if="isDateRangeSelected" class="mb-6">
      <p class="mb-2 text-xs font-medium">
        {{ $t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER') }}
      </p>
      <WootDateRangePicker
        show-range
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateChange"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Report Content -->
    <div v-else-if="reportData">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('LEADS_REPORTS.TOTAL_LEADS') }}
          </p>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ summary.total_leads || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('LEADS_REPORTS.WITH_STRATEGY') }}
          </p>
          <p class="text-2xl font-semibold text-n-blue-11">
            {{ summary.leads_with_strategy || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('LEADS_REPORTS.WITHOUT_STRATEGY') }}
          </p>
          <p class="text-2xl font-semibold text-n-amber-11">
            {{ summary.leads_without_strategy || 0 }}
          </p>
        </div>
      </div>

      <!-- Chart -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('LEADS_REPORTS.CHART_TITLE') }}
        </h3>
        <div v-if="chartData.length" class="h-64">
          <div class="flex items-end justify-between h-full gap-1">
            <div
              v-for="item in chartData"
              :key="item.rawDate"
              class="flex flex-col items-center flex-1"
            >
              <span class="text-xs text-n-slate-11 mb-1">{{ item.count }}</span>
              <div
                class="w-full bg-n-blue-9 rounded-t transition-all duration-300"
                :style="{
                  height: `${getBarHeight(item.count)}%`,
                  minHeight: item.count > 0 ? '4px' : '0',
                }"
              />
              <span
                class="text-xs text-n-slate-11 mt-2 rotate-45 origin-left whitespace-nowrap"
              >
                {{ item.date }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-n-slate-11"
        >
          {{ $t('LEADS_REPORTS.NO_DATA') }}
        </div>
      </div>

      <!-- Strategy Breakdown Table -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('LEADS_REPORTS.BY_STRATEGY') }}
        </h3>
        <div v-if="strategyBreakdown.length" class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                >
                  {{ $t('LEADS_REPORTS.TABLE.STRATEGY') }}
                </th>
                <th
                  class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                >
                  {{ $t('LEADS_REPORTS.TABLE.CAMPAIGN_ID') }}
                </th>
                <th
                  class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                >
                  {{ $t('LEADS_REPORTS.TABLE.LEADS_COUNT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="strategy in strategyBreakdown"
                :key="strategy.strategy_id"
                class="border-b border-n-weak hover:bg-n-alpha-2"
              >
                <td class="py-3 px-4 text-sm text-n-slate-12">
                  {{ strategy.strategy_name }}
                </td>
                <td class="py-3 px-4 text-sm text-n-slate-11">
                  {{ strategy.id_campanha || '-' }}
                </td>
                <td
                  class="py-3 px-4 text-sm text-n-slate-12 text-right font-medium"
                >
                  {{ strategy.leads_count }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-8 text-n-slate-11">
          {{ $t('LEADS_REPORTS.NO_STRATEGY_DATA') }}
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12 text-n-slate-11">
      {{ $t('LEADS_REPORTS.NO_DATA') }}
    </div>
  </div>
</template>
